import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KeyRound, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mêmes codes interdits que côté serveur (chiffres identiques, suites
// croissantes/décroissantes) : vérification côté client uniquement pour un
// retour immédiat, le serveur revalide systématiquement.
const EASY_PINS = new Set();
for (let d = 0; d <= 9; d++) EASY_PINS.add(String(d).repeat(4));
for (let start = 0; start <= 6; start++) {
  const seq = [0, 1, 2, 3].map((i) => String(start + i)).join('');
  EASY_PINS.add(seq);
  EASY_PINS.add(seq.split('').reverse().join(''));
}

const SetPinModal = ({ user, onCreated }) => {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Message d'erreur affiché en permanence dans la modale (contrairement au
  // toast, qui disparaît vite et peut passer inaperçu) tant que le code n'est
  // pas valide.
  const [error, setError] = useState('');

  const sanitize = (value) => value.replace(/\D/g, '').slice(0, 4);

  const handleValidate = async () => {
    setError('');

    if (!/^\d{4}$/.test(pin)) {
      setError('Le code doit comporter exactement 4 chiffres');
      return;
    }
    if (EASY_PINS.has(pin)) {
      setError('Ce code est trop simple (chiffres identiques ou suite logique comme 1234) : choisissez-en un autre');
      return;
    }
    if (!/^\d{4}$/.test(confirmPin)) {
      setError('Merci de confirmer le code dans le second champ');
      return;
    }
    if (pin !== confirmPin) {
      setError('Les deux codes saisis ne correspondent pas');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/set-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      if (res.ok) {
        const updatedUser = await res.json();
        toast.success('Code personnel créé');
        onCreated(updatedUser);
      } else {
        const err = await res.json();
        setError(err.detail || 'Erreur lors de la création du code');
      }
    } catch {
      setError('Erreur de connexion, merci de réessayer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-indigo-600" />
            Création de votre code personnel
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
          <p>
            Bienvenue {user?.name}. Sur les sites équipés d'un compte partagé, ce code à 4 chiffres
            confirmera que c'est bien vous qui rédigez un événement lorsque vous êtes désigné comme
            agent rédacteur. Il est personnel et ne doit être communiqué à personne.
          </p>
          <p className="text-xs text-slate-400">
            Les codes trop simples (0000, 1234, 4321, chiffres répétés ou suites logiques) sont
            interdits.
          </p>

          <div className="space-y-2 pt-2">
            <label className="text-sm font-medium">Nouveau code (4 chiffres)</label>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={(e) => { setPin(sanitize(e.target.value)); setError(''); }}
              placeholder="••••"
              className="text-center tracking-[0.5em] text-lg"
              data-testid="pin-create-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Confirmer le code</label>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={confirmPin}
              onChange={(e) => { setConfirmPin(sanitize(e.target.value)); setError(''); }}
              placeholder="••••"
              className="text-center tracking-[0.5em] text-lg"
              data-testid="pin-confirm-input"
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-2 p-2.5 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300"
              data-testid="pin-create-error"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div>
          <Button
            type="button"
            className="btn-primary w-full mt-2"
            onClick={handleValidate}
            disabled={submitting}
            data-testid="pin-create-validate-btn"
          >
            {submitting ? 'Enregistrement...' : 'Créer mon code'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetPinModal;
